/**
 * The serial drawer's coverage timeline, without React: a year axis from the first of January of
 * the earliest start (or today) to the first of January after the latest end (or today), each
 * coverage as a bar on it, and where today falls. Positions are percentages of the axis.
 */
import type { Coverage } from './api';

const DAY_MS = 86_400_000;

/** `2025-07-17` → its UTC midnight in ms; null for anything that is not a date. */
export function parseDay(iso: string | null | undefined): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!match) return null;
  const day = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(day) ? null : day;
}

/** Today's calendar day (in the browser's time zone) on the same UTC-midnight scale. */
export function calendarDay(now: Date): number {
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

export interface TimelineBar {
  coverage: Coverage;
  /** Where the bar starts and how long it is, in % of the axis; null without both dates. */
  left: number | null;
  width: number | null;
}

export interface Timeline {
  /** The years labelled on the axis (every one, or every other… when there are many). */
  years: Array<{ year: number; left: number }>;
  bars: TimelineBar[];
  /** Today, in % of the axis. */
  today: number;
  /** How today's label sits on its line, so it stays inside the axis near either end. */
  todayAlign: 'start' | 'center' | 'end';
}

/** At most this many year labels; more skip years. */
export const MAX_YEAR_LABELS = 8;

/** The timeline of `coverages` on `now`; null when no coverage has a date. */
export function buildTimeline(coverages: readonly Coverage[], now: Date): Timeline | null {
  const days = coverages
    .flatMap((coverage) => [parseDay(coverage.start_date), parseDay(coverage.end_date)])
    .filter((day): day is number => day !== null);
  if (days.length === 0) return null;
  const today = calendarDay(now);
  const firstYear = new Date(Math.min(...days, today)).getUTCFullYear();
  const lastYear = new Date(Math.max(...days, today)).getUTCFullYear();
  const from = Date.UTC(firstYear, 0, 1);
  const span = Date.UTC(lastYear + 1, 0, 1) - from;
  const at = (day: number) => ((day - from) / span) * 100;

  const yearCount = lastYear - firstYear + 1;
  const step = Math.ceil(yearCount / MAX_YEAR_LABELS);
  const years = Array.from({ length: yearCount }, (_, i) => firstYear + i)
    .filter((year) => (year - firstYear) % step === 0)
    .map((year) => ({ year, left: at(Date.UTC(year, 0, 1)) }));

  const bars = coverages.map((coverage) => {
    const start = parseDay(coverage.start_date);
    const end = parseDay(coverage.end_date);
    if (start === null || end === null || end < start) return { coverage, left: null, width: null };
    // A coverage that starts and ends on one day still shows as a sliver.
    return { coverage, left: at(start), width: at(Math.max(end, start + DAY_MS)) - at(start) };
  });

  const todayAt = at(today);
  const todayAlign = todayAt < 12 ? 'start' : todayAt > 88 ? 'end' : 'center';
  return { years, bars, today: todayAt, todayAlign };
}
